import { useEffect, useState } from "react";

import fetch from "cross-fetch";

import {
    useLanguage,
    useAnalyticsAttributes,
    useAnalytics,
    Picture
} from "../../components";
import { UiNavBlogArticleModel } from "../../models";
import { renderViewModel } from "../../render-view-model";
import { HeaderTabLink } from "../UiHeaderNav/shared";

import parseRss from "./parseRss";
import {
    PlaceholderFrame,
    PlaceholderImage,
    Eyebrow,
    Title,
    Link,
    FallbackBanner
} from "./UiNavBlogArticle.styles";

type BlogArticle = {
    title?: string;
    eyebrow?: string;
    imageUrl?: string;
    url?: string;
};

type UiNavBlogArticleProps = {
    readonly model: UiNavBlogArticleModel;
    readonly isShowing?: boolean;
    readonly tabIndex?: number;
};

const UiNavBlogArticle = ({
    model: {
        id,
        type,
        content: { entryTitle, fallback, title, cta }
    },
    isShowing = true,
    tabIndex
}: UiNavBlogArticleProps) => {
    const feeds: { [index: string]: string } = {
        de: "https://blog.trello.com/de/tag/anwendungsf%C3%A4lle/rss.xml",
        en: "https://blog.trello.com/tag/use-cases/rss.xml",
        es: "https://blog.trello.com/es/tag/ejemplos-de-uso/rss.xml",
        fr: "https://blog.trello.com/fr/tag/id%C3%A9es-tableaux/rss.xml",
        pt: "https://blog.trello.com/br/tag/exemplos-de-uso/rss.xml"
    };

    const [article, setArticle] = useState<BlogArticle>();
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();
    const lang = useLanguage().substring(0, 2);
    const [error, setError] = useState(false);
    const feed = feeds[lang];

    useEffect(() => {
        let cancelled = false;

        async function fetchArticle(feed: string) {
            try {
                const response = await fetch(feed);
                const text = await response.text();
                const article = parseRss(text);
                if (!cancelled) {
                    setArticle(article);
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setError(true);
                }
            }
        }

        if (isShowing && feed && !article && !error) {
            void fetchArticle(feed);
        }

        return () => {
            cancelled = true;
        };
    }, [feed, isShowing, article, error]);

    //lang is not supported or there's been an error, render fallback UiNavBanner
    if (!feed || error) {
        return <FallbackBanner>{renderViewModel(fallback)}</FallbackBanner>;
    }

    //article is still loading, render placeholder skeleton
    if (!article) {
        return (
            <PlaceholderFrame data-testid="ui-blog-article">
                <PlaceholderImage />
                <Eyebrow isPlaceholder />
                <Title isPlaceholder />
            </PlaceholderFrame>
        );
    }

    //render article loaded from RSS feed
    return (
        <>
            <h3>{title}</h3>
            <Link
                href={article.url}
                onClick={() =>
                    sendTrackEvent("clickedUiBlogArticle", {
                        ...analyticsAttributes,
                        event: "clicked",
                        eventComponent: type,
                        href: article.url,
                        label: article.title,
                        labelEnglish: entryTitle,
                        uuid: id
                    })
                }
                tabIndex={tabIndex}
                target="_blank"
                data-testid="ui-blog-article"
            >
                {article.imageUrl && (
                    <Picture
                        image={{
                            id: id,
                            url: article.imageUrl,
                            title: article.title || "",
                            width: 0,
                            height: 0
                        }}
                        decorative={true}
                    />
                )}
                <Eyebrow>{article.eyebrow}</Eyebrow>
                <Title>{article.title}</Title>
            </Link>
            {cta.content && (
                <HeaderTabLink
                    url={cta.content.url}
                    text={cta.content.text}
                    ariaLabel={cta.content.ariaLabel}
                    onClick={() =>
                        sendTrackEvent("clickedGlobalNavSecondaryLink", {
                            ...analyticsAttributes,
                            event: "clicked",
                            eventComponent: "globalNavSecondaryLink",
                            label: cta.content.text,
                            labelEnglish: cta.content.entryTitle,
                            parentLabel: title,
                            href: cta.content.url,
                            uuid: cta.id
                        })
                    }
                />
            )}
        </>
    );
};

export default UiNavBlogArticle;
